import React from 'react'
import View from '../../../../../components/pdf/View'
import Text from '../../../../../components/pdf/Text'
import Line from '../../../../../components/pdf/Line'
import Separator from '../../../../../components/pdf/Separator'
import { Table, TableCol, TableRow } from '../../../../../components/pdf/Table'
import Grid, { Col } from '../../../../../components/pdf/Grid'

import { useServiceOrder } from '../../_load/ServiceOrderLoader'
import {
  filterRepliesByParentReference,
  findQuestionById,
  findReplyByQuestionId,
  getDifferenceBetweenArrays,
  getRepliesFromBlockQuestion,
  getReplyValue,
  toCurrency
} from '../../_helpers/pdfHelpers'
import ContratoSection from './sections/ContratoSection'
import TeamSection from './sections/TeamSection'

const PropostaTemplate = () => {
  const { replies, checklist, name: clientName } = useServiceOrder()

  const hasCustomDocs = findReplyByQuestionId(replies, 4001)?.value
  const hasIntegration = findReplyByQuestionId(replies, 4011)?.value
  const hasDashboard = findReplyByQuestionId(replies, 4021)?.value
  const hasSupport = findReplyByQuestionId(replies, 4067)?.value
  const paymentDay = findReplyByQuestionId(replies, 4102)?.value
  const additionalDescription = findReplyByQuestionId(replies, 4104)?.value
  const additionalAmount = findReplyByQuestionId(replies, 4185)?.value
  const contractType = findReplyByQuestionId(replies, 4105)?.value?.name
  const paymentMethod = getReplyValue(findReplyByQuestionId(replies, 3991))?.name

  const razaoSocial = getReplyValue(findReplyByQuestionId(replies, 4084))
  const cnpj = getReplyValue(findReplyByQuestionId(replies, 4086))
  const nomeFantasia = getReplyValue(findReplyByQuestionId(replies, 4085))
  const inscricaoEstadual = getReplyValue(findReplyByQuestionId(replies, 4087))
  const inscricaoMunicipal = getReplyValue(findReplyByQuestionId(replies, 4088))
  const address = getReplyValue(findReplyByQuestionId(replies, 4089))?.search
  const complement = getReplyValue(findReplyByQuestionId(replies, 4089))?.complement || null

  const clientInfo = { razaoSocial, cnpj, nomeFantasia, inscricaoEstadual, inscricaoMunicipal, address, complement }

  const featuresIncludedReplies = findReplyByQuestionId(replies, 3988)?.value
  const featuresIncludedQuestions = findQuestionById(checklist?.questions, 3988)?.options
  const featuresIncludedDifference = getDifferenceBetweenArrays(featuresIncludedQuestions, featuresIncludedReplies)

  const extraServicesReplies = findReplyByQuestionId(replies, 3989)?.value ?? [
    { name: 'Nenhum serviço extra contratado' }
  ]
  const extraServicesQuestions = findQuestionById(checklist?.questions, 3989)?.options ?? []
  const extraServicesDifference = getDifferenceBetweenArrays(extraServicesQuestions, extraServicesReplies)

  const getPaymentMethodAndValue = (card, billet) => {
    return paymentMethod === 'Cartão' ? `${toCurrency(card)} (Cartão)` : `${toCurrency(billet)} (Boleto)`
  }

  const customDocBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 4002)
    const customDocReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const customDocResult = {}
    customDocReplies.forEach(customDocReply => {
      const blockIndex = customDocReply.blockIndex
      let customDocData = customDocResult[blockIndex]
      if (!customDocData) {
        const blockReplies = getRepliesFromBlockQuestion(customDocReplies, blockIndex)
        customDocData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 4004)),
          type: 'Documento customizado',
          description: getReplyValue(findReplyByQuestionId(blockReplies, 4005)),
          hours: getReplyValue(findReplyByQuestionId(blockReplies, 4006)),
          status: getReplyValue(findReplyByQuestionId(blockReplies, 4096))?.name
        }
        customDocResult[blockIndex] = customDocData
      }
    })
    return Object.values(customDocResult)
  }

  const integrationBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 4012)
    const integrationReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const integrationResult = {}
    integrationReplies.forEach(integrationReply => {
      const blockIndex = integrationReply.blockIndex
      let integrationData = integrationResult[blockIndex]
      if (!integrationData) {
        const blockReplies = getRepliesFromBlockQuestion(integrationReplies, blockIndex)

        integrationData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 4014)),
          type: 'Integração',
          description: getReplyValue(findReplyByQuestionId(blockReplies, 4015)),
          hours: getReplyValue(findReplyByQuestionId(blockReplies, 4016)),
          status: getReplyValue(findReplyByQuestionId(blockReplies, 4100))?.name
        }
        integrationResult[blockIndex] = integrationData
      }
    })
    return Object.values(integrationResult)
  }

  const dashboardBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 4022)
    const dashboardReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const dashboardResult = {}
    dashboardReplies.forEach(dashboardReply => {
      const blockIndex = dashboardReply.blockIndex
      let dashboardData = dashboardResult[blockIndex]
      if (!dashboardData) {
        const blockReplies = getRepliesFromBlockQuestion(dashboardReplies, blockIndex)

        dashboardData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 4024)),
          type: 'Dashboard customizado',
          description: getReplyValue(findReplyByQuestionId(blockReplies, 4025)),
          hours: getReplyValue(findReplyByQuestionId(blockReplies, 4026)),
          status: getReplyValue(findReplyByQuestionId(blockReplies, 4101))?.name
        }
        dashboardResult[blockIndex] = dashboardData
      }
    })
    return Object.values(dashboardResult)
  }

  const supportBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 4068)
    const supportReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const supportResult = {}
    supportReplies.forEach(supportReply => {
      const blockIndex = supportReply.blockIndex
      let supportData = supportResult[blockIndex]
      if (!supportData) {
        const blockReplies = getRepliesFromBlockQuestion(supportReplies, blockIndex)

        supportData = {
          type: 'Pacote de Sustentação',
          description: 'Horas/Mês',
          quantity: getReplyValue(findReplyByQuestionId(blockReplies, 4070)),
          hoursConsumed: getReplyValue(findReplyByQuestionId(blockReplies, 4071)),
          hoursToConsume: getReplyValue(findReplyByQuestionId(blockReplies, 4072))
        }
        supportResult[blockIndex] = supportData
      }
    })
    return Object.values(supportResult)
  }

  const userPlanBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 4032)
    const userPlanReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const userPlanResult = {}
    userPlanReplies.forEach(userPlanReply => {
      const blockIndex = userPlanReply.blockIndex
      let userPlanData = userPlanResult[blockIndex]
      if (!userPlanData) {
        const blockReplies = getRepliesFromBlockQuestion(userPlanReplies, blockIndex)
        userPlanData = {
          type: 'Usuário',
          description: 'Usuário/Mês',
          quantity: getReplyValue(findReplyByQuestionId(blockReplies, 4033)),
          additionalAmount: getReplyValue(findReplyByQuestionId(blockReplies, 4147)) || 0,
          amountCard: getReplyValue(findReplyByQuestionId(blockReplies, 4148)) || 0,
          amountBillet: getReplyValue(findReplyByQuestionId(blockReplies, 4149)) || 0
        }
        userPlanResult[blockIndex] = userPlanData
      }
    })
    return Object.values(userPlanResult)
  }

  const placePlanBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 4040)
    const placePlanReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const placePlanResult = {}
    placePlanReplies.forEach(placePlanReply => {
      const blockIndex = placePlanReply.blockIndex
      let placePlanData = placePlanResult[blockIndex]
      if (!placePlanData) {
        const blockReplies = getRepliesFromBlockQuestion(placePlanReplies, blockIndex)
        placePlanData = {
          type: 'Local',
          description: 'Local/Mês',
          quantity: getReplyValue(findReplyByQuestionId(blockReplies, 4150)),
          additionalAmount: getReplyValue(findReplyByQuestionId(blockReplies, 4151)) || 0,
          amountCard: getReplyValue(findReplyByQuestionId(blockReplies, 4152)) || 0,
          amountBillet: getReplyValue(findReplyByQuestionId(blockReplies, 4153)) || 0,
          typeOfPayment: getReplyValue(findReplyByQuestionId(blockReplies, 4048))?.name
        }
        placePlanResult[blockIndex] = placePlanData
      }
    })
    return Object.values(placePlanResult)
  }

  const serviceOrderPlanBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 4049)
    const serviceOrderPlanReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const serviceOrderPlanResult = {}
    serviceOrderPlanReplies.forEach(serviceOrderPlanReply => {
      const blockIndex = serviceOrderPlanReply.blockIndex
      let serviceOrderPlanData = serviceOrderPlanResult[blockIndex]
      if (!serviceOrderPlanData) {
        const blockReplies = getRepliesFromBlockQuestion(serviceOrderPlanReplies, blockIndex)
        serviceOrderPlanData = {
          type: 'Atividade',
          description: 'Atividade/Mês',
          quantity: getReplyValue(findReplyByQuestionId(blockReplies, 4154)),
          additionalAmount: getReplyValue(findReplyByQuestionId(blockReplies, 4155)) || 0,
          amountCard: getReplyValue(findReplyByQuestionId(blockReplies, 4156)) || 0,
          amountBillet: getReplyValue(findReplyByQuestionId(blockReplies, 4157)) || 0,
          typeOfPayment: getReplyValue(findReplyByQuestionId(blockReplies, 4057))?.name
        }
        serviceOrderPlanResult[blockIndex] = serviceOrderPlanData
      }
    })
    return Object.values(serviceOrderPlanResult)
  }

  const deploymentClientTeamBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 4076)
    const deploymentClientTeamReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const deploymentClientTeamResult = {}
    deploymentClientTeamReplies.forEach(deploymentClientTeamReply => {
      const blockIndex = deploymentClientTeamReply.blockIndex
      let deploymentClientTeamData = deploymentClientTeamResult[blockIndex]
      if (!deploymentClientTeamData) {
        const blockReplies = getRepliesFromBlockQuestion(deploymentClientTeamReplies, blockIndex)
        deploymentClientTeamData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 4077)),
          phone: getReplyValue(findReplyByQuestionId(blockReplies, 4078)),
          email: getReplyValue(findReplyByQuestionId(blockReplies, 4079))
        }
        deploymentClientTeamResult[blockIndex] = deploymentClientTeamData
      }
    })
    return Object.values(deploymentClientTeamResult)
  }

  const financialClientTeamBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 4080)
    const financialClientTeamReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const financialClientTeamResult = {}
    financialClientTeamReplies.forEach(financialClientTeamReply => {
      const blockIndex = financialClientTeamReply.blockIndex
      let financialClientTeamData = financialClientTeamResult[blockIndex]
      if (!financialClientTeamData) {
        const blockReplies = getRepliesFromBlockQuestion(financialClientTeamReplies, blockIndex)
        financialClientTeamData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 4081)),
          phone: getReplyValue(findReplyByQuestionId(blockReplies, 4082)),
          email: getReplyValue(findReplyByQuestionId(blockReplies, 4083))
        }
        financialClientTeamResult[blockIndex] = financialClientTeamData
      }
    })
    return Object.values(financialClientTeamResult)
  }

  const customDocReplies = customDocBlock()
  const integrationReplies = integrationBlock()
  const dashboardReplies = dashboardBlock()
  const supportReplies = supportBlock()
  const userPlanReplies = userPlanBlock()
  const placePlanReplies = placePlanBlock()
  const serviceOrderPlanReplies = serviceOrderPlanBlock()

  const deploymentClientTeamReplies = deploymentClientTeamBlock()
  const financialClientTeamReplies = financialClientTeamBlock()

  const monthlyPaymentResume = [...userPlanReplies, ...placePlanReplies, ...serviceOrderPlanReplies]

  const monthlyPaymentCard = monthlyPaymentResume.reduce((total, service) => total + service.amountCard, 0)
  const monthlyPaymentBillet = monthlyPaymentResume.reduce((total, service) => total + service.amountBillet, 0)

  return (
    <View pageBreakAfter>
      <Text semibold size="h2">
        {contractType} Btime | {clientName}
      </Text>
      <Line />
      <Separator weight={15} />

      <ContratoSection clientInfo={clientInfo} />

      <Separator weight={15} />

      <div id="escopo-projeto">
        <Text semibold size="h3">
          Escopo do Projeto
        </Text>
        <Line />
        <Separator weight={5} />

        <div id="escopo-projeto-1">
          <Text semibold size="h5">
            🙋 Quais são as dores do cliente e como resolveremos elas?
          </Text>
          <Separator weight={3} />
          <Table width="100%" color="#333333">
            <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
              <TableCol width="50%">
                <Text semibold>Sem btime</Text>
              </TableCol>
              <TableCol color="#333333">
                <Text semibold>Com btime</Text>
              </TableCol>
            </TableRow>
            <TableRow color="#333333">
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Processo no papel:</b> Todo registro de informações da operação é feita no papel, o que gera muita
                  perda de dados e inconsistência de informações.
                </Text>
              </TableCol>
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Digitalização:</b> A btime digitaliza toda a coleta de dados e garante padronização de dados e
                  geração de relatórios em tempo real.
                </Text>
              </TableCol>
            </TableRow>
            <TableRow color="#333333">
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Dificuldade na gestão:</b> Com os dados despadronizados e em papel, você não consegue fazer uma
                  gestão eficiente dos seus processos e não consegue saber o que está acontecendo na sua operação.
                </Text>
              </TableCol>
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Gestão em tempo real:</b> Com o seu processos digitalizados, você consegue fazer a gestão da sua
                  operação completa em um só lugar, acompanhando a evolução de cada etapa em tempo real.
                </Text>
              </TableCol>
            </TableRow>
            <TableRow color="#333333">
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Falta de Relatórios:</b> Sem padronização e centralização dos dados, você não consegue ter
                  relatórios e dashboards para auxiliar na tomada de decisão, te deixando sem visibilidade do que está
                  acontecendo.
                </Text>
              </TableCol>
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Relatórios Automáticos:</b> Geramos relatórios e dashboards automáticos que te municiam de
                  informações estratégias para que sua tomada de decisão seja precisa e ágil.
                </Text>
              </TableCol>
            </TableRow>
            <TableRow color="#333333">
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Trabalho Manual:</b> Você e sua equipe perdem horas e horas em trabalhos manuais e operacionais,
                  reescrevendo dados do papel, caçando histórico em conversas do WhatsApp e não sobra tempo para fazer o
                  trabalho que realmente importa.
                </Text>
              </TableCol>
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Automações:</b> Automatizamos todos os seus trabalhos manuais e operacionais, não só coletando
                  dados, gerando relatórios e dashboards instantaneamente, mas também automatizando ações e análises que
                  hoje são feito por pessoas, liberando tempo para você e sua equipe focaram no seu negócio.
                </Text>
              </TableCol>
            </TableRow>
          </Table>
        </div>
        <Separator weight={10} />

        <div id="escopo-projeto-3">
          <Text semibold size="h5">
            🧑‍💻 Quais são as funcionalidades inclusas no seu plano?
          </Text>
          <Separator weight={3} />

          <Grid direction="row" align="start" style={{ gap: 10 }}>
            <Col width="50%" gutter={10} style={{ background: '#dcfce7', borderRadius: 10 }}>
              <Text semibold>✅ Incluso:</Text>
              <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
                {featuresIncludedReplies.map((reply, index) => {
                  return (
                    <li key={index}>
                      <Text align="justify">{reply.name}</Text>
                    </li>
                  )
                })}
              </ul>
            </Col>
            <Col width="50%" gutter={10} style={{ background: '#fee2e2', borderRadius: 10 }}>
              <Text semibold>🔴 Não Incluso:</Text>
              <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
                {featuresIncludedDifference.map((reply, index) => {
                  return (
                    <li key={index}>
                      <Text align="justify">{reply.name}</Text>
                    </li>
                  )
                })}
              </ul>
            </Col>
          </Grid>
        </div>
        <Separator weight={10} />

        <div id="escopo-projeto-4">
          <Text semibold size="h5">
            🧑‍💻 Quais são os serviços extras inclusos no seu plano? (Serviços contratados à parte)
          </Text>
          <Separator weight={3} />

          <Grid direction="row" align="start" style={{ gap: 10 }}>
            <Col width="50%" gutter={10} style={{ background: '#dcfce7', borderRadius: 10 }}>
              <Text semibold>✅ Incluso:</Text>
              <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
                {extraServicesReplies.map((reply, index) => {
                  return (
                    <li key={index}>
                      <Text align="justify">{reply.name}</Text>
                    </li>
                  )
                })}
              </ul>
            </Col>
            <Col width="50%" gutter={10} style={{ background: '#fee2e2', borderRadius: 10 }}>
              <Text semibold>🔴 Não Incluso:</Text>
              <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
                {extraServicesDifference.map((reply, index) => {
                  return (
                    <li key={index}>
                      <Text align="justify">{reply.name}</Text>
                    </li>
                  )
                })}
              </ul>
            </Col>
          </Grid>
        </div>
      </div>
      <Separator weight={15} />

      <TeamSection
        clientName={clientName}
        deploymentClientTeamReplies={deploymentClientTeamReplies}
        financialClientTeamReplies={financialClientTeamReplies}
      />

      <div id="resumo-investimento">
        <Text semibold size="h3">
          Resumo do Investimento
        </Text>
        <Line />
        <Separator weight={3} />
        <Text align="justify">
          Resumo dos pacotes completos de Mensalidade. Descrição completa de cada pacote nas seções seguintes.
        </Text>
        <Separator weight={5} />

        <div id="resumo-investimento-2">
          <Text semibold size="h5">
            Mensalidade - {getPaymentMethodAndValue(monthlyPaymentCard, monthlyPaymentBillet)}
          </Text>
          <Text>Itens envolvidos no pacote de cobrança mensal: (Detalhamento na sessão seguinte)</Text>
          <Separator weight={3} />
          <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
            {monthlyPaymentResume.map((item, index) => {
              return (
                <li key={index} style={{ marginBottom: 5 }}>
                  <Text>
                    <b>Tipo de cobrança</b>: {item.type}
                  </Text>
                  <Text>
                    <b>Quantidade contratada</b>: {item.quantity} {item.description}
                  </Text>
                  <Text>
                    <b>Valor por adicional</b>: {toCurrency(item.additionalAmount)}
                  </Text>
                </li>
              )
            })}
            <li style={{ marginBottom: 5 }}>
              <Text>
                <b>Método de pagamento</b>: {paymentMethod}
              </Text>
            </li>
            <li style={{ marginBottom: 5 }}>
              <Text>
                <b>Dia de pagamento</b>: {paymentDay}
              </Text>
            </li>
            {additionalAmount && (
              <li style={{ marginBottom: 5 }}>
                <Text>
                  <b>Valor adicional na mensalidade</b>: {toCurrency(additionalAmount)}
                </Text>
              </li>
            )}
            {additionalDescription && (
              <li style={{ marginBottom: 5 }}>
                <Text>
                  <b>Descritivo do valor adicional da mensalidade</b>: {additionalDescription}
                </Text>
              </li>
            )}
          </ul>
        </div>
      </div>

      <Separator weight={10} />

      <div id="detalhes-implantacao">
        {hasCustomDocs && (
          <>
            <Separator weight={10} />
            <div id="detalhes-implantacao-3">
              <Text semibold size="h5">
                Detalhamento dos valores da criação de Documentos Customizados
              </Text>

              <Separator weight={2} />

              <Table width="100%" color="#333333">
                <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
                  <TableCol color="#333333" width="40%">
                    <Text semibold>Documento</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Descrição</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total de Horas</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Status</Text>
                  </TableCol>
                </TableRow>
                {customDocReplies.map((reply, index) => {
                  return (
                    <TableRow color="#333333" key={index}>
                      <TableCol color="#333333">
                        <Text>{reply.name}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.description}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.hours}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.status}</Text>
                      </TableCol>
                    </TableRow>
                  )
                })}
              </Table>
            </div>
          </>
        )}

        {hasIntegration && (
          <>
            <Separator weight={10} />
            <div id="detalhes-implantacao-4">
              <Text semibold size="h5">
                Detalhamento dos valores de Integração
              </Text>
              <Separator weight={2} />

              <Table width="100%" color="#333333">
                <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
                  <TableCol color="#333333" width="40%">
                    <Text semibold>Sistema</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Descrição</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total de Horas</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Status</Text>
                  </TableCol>
                </TableRow>
                {integrationReplies.map((reply, index) => {
                  return (
                    <TableRow color="#333333" key={index}>
                      <TableCol color="#333333">
                        <Text>{reply.name}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.description}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.hours}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.status}</Text>
                      </TableCol>
                    </TableRow>
                  )
                })}
              </Table>
            </div>
          </>
        )}

        {hasDashboard && (
          <>
            <Separator weight={10} />
            <div id="detalhes-implantacao-5">
              <Text semibold size="h5">
                Detalhamento dos valores da criação de Dashboards Customizados
              </Text>

              <Separator weight={2} />

              <Table width="100%" color="#333333">
                <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
                  <TableCol color="#333333" width="40%">
                    <Text semibold>Dashboard</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Descrição</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total de Horas</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Status</Text>
                  </TableCol>
                </TableRow>
                {dashboardReplies.map((reply, index) => {
                  return (
                    <TableRow color="#333333" key={index}>
                      <TableCol color="#333333">
                        <Text>{reply.name}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.description}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.hours}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.status}</Text>
                      </TableCol>
                    </TableRow>
                  )
                })}
              </Table>
            </div>
          </>
        )}

        {hasSupport && (
          <>
            <Separator weight={10} />
            <div id="detalhes-servicos-extras-3">
              <Text semibold size="h5">
                Detalhamento do pacote de Sustentação
              </Text>

              <Separator weight={2} />

              <Table width="100%" color="#333333">
                <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
                  <TableCol color="#333333" width="33%">
                    <Text semibold>Total de Horas</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total de Horas Consumidas</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total de Horas à Consumir</Text>
                  </TableCol>
                </TableRow>
                {supportReplies.map((reply, index) => {
                  return (
                    <TableRow color="#333333" key={index} bgColor={reply.selectedPlan ? '#dcfce7' : ''}>
                      <TableCol color="#333333">
                        <Text>{reply.quantity}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.hoursConsumed}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.hoursToConsume}</Text>
                      </TableCol>
                    </TableRow>
                  )
                })}
              </Table>
            </div>
          </>
        )}
      </div>

      <Separator weight={15} />
    </View>
  )
}

export default PropostaTemplate
